import Service from '@ember/service';
import { computed } from '@ember/object';
import { service } from '@ember/service';
import ConfigurationData from '../data/configurationdata';
import Consts from '../utils/constants';
import cloneDeep from 'lodash.clonedeep';
import merge from 'lodash.merge';

export default class CircuitConfigurationsService extends Service {
    @service selectionData;
    @service intl;

    multiplexed = false;

    _getAllCircuitConfigs() {
        return ConfigurationData.circuitConfigs.map((circuitConfig) => this.get(circuitConfig));
    }

    _mapCircuitConfig(circuitConfig) {
        let config = cloneDeep(circuitConfig);
        if (!config.svg) {
            return config;
        }

        if (config.hasInternalMux && this.selectionData.selectedAdc.internalMux) {
            config.svg += `-${Consts.SVG.PARTS.InternalMux}`;
        } else if (this.multiplexed) {
            config.svg += `-${Consts.SVG.PARTS.InputMux}`;
        }

        if (
            this.selectionData.selectedDriver.fixedGain &&
            this.circuitParts.driverType === Consts.DRIVER_TYPE.DIFF_AMP
        ) {
            config.svg = config.svg.replace(Consts.SVG.PARTS.DiffAmp, Consts.SVG.PARTS.DiffAmpFixedGain);
        }

        if (['AD4080', 'AD4081', 'AD4084'].includes(this.selectionData.selectedAdc.partCore)) {
            config.svg += `-AD408x`;
        }

        return config;
    }

    @computed('circuitParts', 'multiplexed', 'selectionData.selectedAdc.partCore') get currentCircuitConfig() {
        let config = cloneDeep(ConfigurationData.circuitDefaults),
            foundConfigs = this._getAllCircuitConfigs()
                .filter((circuitConfig) => circuitConfig.isCurrent)
                .map((circuitConfig) => this._mapCircuitConfig(circuitConfig));

        if (foundConfigs && foundConfigs.length) {
            let currentConfig = merge(config, foundConfigs[0]);
            return currentConfig;
        }

        return config;
    }

    @computed('selectionData.{selectedAdc.type,selectedSourceType.id,selectedDriver.type,selectedTopology.id,gain}')
    get circuitParts() {
        return {
            adcType: this.selectionData.selectedAdc.type,
            sourceType: this.selectionData.selectedSourceType.id,
            driverType: this.selectionData.selectedDriver.type,
            topology: this.selectionData.actualTopologyId,
        };
    }

    /**
     * Configurations
     */

    /**
     * SEADC
     * SESource
     * SEAmp
     */

    @computed('circuitParts')
    get SEADC_SESource_SEAmp_CONFIG() {
        let circuitParts = this.circuitParts,
            isCurrent =
                circuitParts.adcType === Consts.ADC_TYPE.SE &&
                circuitParts.sourceType === Consts.SOURCE_TYPE.SE &&
                circuitParts.driverType === Consts.DRIVER_TYPE.AMP;

        return {
            isCurrent: isCurrent,
            value: Consts.CONFIGURATION.SEADC,
        };
    }

    @computed('circuitParts.topology', 'SEADC_SESource_SEAmp_CONFIG')
    get SEADC_SESource_SEAmp_Inverting() {
        let config = this.SEADC_SESource_SEAmp_CONFIG,
            topology = this.circuitParts.topology;

        return {
            isCurrent: config.isCurrent && topology === Consts.TOPOLOGY.INVERTING,
            svg: Consts.SVG.SEADC_SESource_SEAmp_Inverting,
            config: config.value,
            hasInternalMux: true,
        };
    }

    @computed('circuitParts.topology', 'SEADC_SESource_SEAmp_CONFIG')
    get SEADC_SESource_SEAmp_Follower() {
        let config = this.SEADC_SESource_SEAmp_CONFIG,
            topology = this.circuitParts.topology;

        return {
            isCurrent: config.isCurrent && topology === Consts.TOPOLOGY.FOLLOWER,
            svg: Consts.SVG.SEADC_SESource_SEAmp_Follower,
            config: config.value,
            hasInternalMux: true,
        };
    }

    @computed('circuitParts.topology', 'SEADC_SESource_SEAmp_CONFIG')
    get SEADC_SESource_SEAmp_NonInverting() {
        let config = this.SEADC_SESource_SEAmp_CONFIG,
            topology = this.circuitParts.topology;

        return {
            isCurrent: config.isCurrent && topology === Consts.TOPOLOGY.NON_INVERTING,
            svg: Consts.SVG.SEADC_SESource_SEAmp_NonInverting,
            config: config.value,
            hasInternalMux: true,
        };
    }

    /**
     * DiffADC
     * SEAmp
     * SESource
     */
    @computed('circuitParts') get DiffADC_SEAmp_SESource_CONFIG() {
        let circuitParts = this.circuitParts,
            isCurrent =
                circuitParts.adcType === Consts.ADC_TYPE.DIFF &&
                circuitParts.sourceType === Consts.SOURCE_TYPE.SE &&
                circuitParts.driverType === Consts.DRIVER_TYPE.AMP;

        return {
            isCurrent: isCurrent,
            value: Consts.CONFIGURATION.DiffADC_SEAmp_SESource,
        };
    }

    @computed('circuitParts.topology', 'DiffADC_SEAmp_SESource_CONFIG') get DiffADC_SESource_SEAmp_Inverting() {
        let topology = this.circuitParts.topology,
            config = this.DiffADC_SEAmp_SESource_CONFIG;

        return {
            isCurrent: config.isCurrent && topology === Consts.TOPOLOGY.INVERTING,
            svg: Consts.SVG.DiffADC_SESource_SEAmp_Inverting,
            secondDriver: true,
            config: config.value,
        };
    }

    @computed('DiffADC_SEAmp_SESource_CONFIG', 'selectionData.essentiallyFollower')
    get DiffADC_SESource_SEAmp_Follower() {
        let config = this.DiffADC_SEAmp_SESource_CONFIG,
            essentiallyFollower = this.selectionData.essentiallyFollower;

        return {
            isCurrent: config.isCurrent && essentiallyFollower,
            svg: Consts.SVG.DiffADC_SESource_SEAmp_Follower,
            secondDriver: true,
            config: config.value,
        };
    }

    @computed('circuitParts.topology', 'DiffADC_SEAmp_SESource_CONFIG') get DiffADC_SESource_SEAmp_NonInverting() {
        let config = this.DiffADC_SEAmp_SESource_CONFIG,
            topology = this.circuitParts.topology;

        return {
            isCurrent: config.isCurrent && topology === Consts.TOPOLOGY.NON_INVERTING,
            svg: Consts.SVG.DiffADC_SESource_SEAmp_NonInverting,
            secondDriver: true,
            config: config.value,
        };
    }

    /**
     * DiffADC
     * DiffAmp
     * SESource
     */

    @computed('circuitParts') get DiffADC_DiffAmp_SESource_CONFIG() {
        let circuitParts = this.circuitParts,
            isCurrent =
                circuitParts.adcType === Consts.ADC_TYPE.DIFF &&
                circuitParts.sourceType === Consts.SOURCE_TYPE.SE &&
                circuitParts.driverType === Consts.DRIVER_TYPE.DIFF_AMP;

        return {
            isCurrent: isCurrent,
            value: Consts.CONFIGURATION.DiffADC_DiffAmp_SESource,
        };
    }

    @computed('circuitParts.topology', 'DiffADC_DiffAmp_SESource_CONFIG')
    get DiffADC_SESource_DiffAmp_Inverting() {
        let config = this.DiffADC_DiffAmp_SESource_CONFIG,
            topology = this.circuitParts.topology;

        return {
            isCurrent: config.isCurrent && topology === Consts.TOPOLOGY.INVERTING,
            svg: Consts.SVG.DiffADC_SESource_DiffAmp_Inverting,
            nInvertingEnabled: false,
            config: config.value,
        };
    }

    /**
     * DiffADC
     * SEAmp
     * DiffSource
     */

    @computed('circuitParts') get DiffADC_SEAmp_DiffSource_CONFIG() {
        let circuitParts = this.circuitParts,
            isCurrent =
                circuitParts.adcType === Consts.ADC_TYPE.DIFF &&
                circuitParts.sourceType === Consts.SOURCE_TYPE.DIFF &&
                circuitParts.driverType === Consts.DRIVER_TYPE.AMP;

        return {
            isCurrent: isCurrent,
            value: Consts.CONFIGURATION.DiffADC_SEAmp_DiffSource,
        };
    }

    @computed('circuitParts.topology', 'DiffADC_SEAmp_DiffSource_CONFIG')
    get DiffADC_DiffSource_SEAmp_Inverting() {
        let config = this.DiffADC_SEAmp_DiffSource_CONFIG,
            topology = this.circuitParts.topology;

        return {
            isCurrent: config.isCurrent && topology === Consts.TOPOLOGY.INVERTING,
            svg: Consts.SVG.DiffADC_DiffSource_SEAmp_Inverting,
            config: config.value,
        };
    }

    @computed('circuitParts.topology', 'DiffADC_SEAmp_DiffSource_CONFIG')
    get DiffADC_DiffSource_SEAmp_Follower() {
        let config = this.DiffADC_SEAmp_DiffSource_CONFIG,
            topology = this.circuitParts.topology;

        return {
            isCurrent: config.isCurrent && topology === Consts.TOPOLOGY.FOLLOWER,
            svg: Consts.SVG.DiffADC_DiffSource_SEAmp_Follower,
            config: config.value,
        };
    }

    @computed('circuitParts.topology', 'DiffADC_SEAmp_DiffSource_CONFIG')
    get DiffADC_DiffSource_SEAmp_Non_Inverting() {
        let config = this.DiffADC_SEAmp_DiffSource_CONFIG,
            topology = this.circuitParts.topology;

        return {
            isCurrent: config.isCurrent && topology === Consts.TOPOLOGY.NON_INVERTING,
            svg: Consts.SVG.DiffADC_DiffSource_SEAmp_NonInverting,
            config: config.value,
        };
    }

    /**
     * DiffADC
     * DiffAmp
     * DiffSource
     */
    @computed('circuitParts') get DiffADC_DiffAmp_DiffSource_CONFIG() {
        let circuitParts = this.circuitParts,
            isCurrent =
                circuitParts.adcType === Consts.ADC_TYPE.DIFF &&
                circuitParts.sourceType === Consts.SOURCE_TYPE.DIFF &&
                circuitParts.driverType === Consts.DRIVER_TYPE.DIFF_AMP;

        return {
            isCurrent: isCurrent,
            value: Consts.CONFIGURATION.DiffADC_DiffAmp_DiffSource,
        };
    }

    @computed('circuitParts.topology', 'DiffADC_DiffAmp_DiffSource_CONFIG')
    get DiffADC_DiffSource_DiffAmp_Inverting() {
        let config = this.DiffADC_DiffAmp_DiffSource_CONFIG,
            topology = this.circuitParts.topology;

        return {
            isCurrent: config.isCurrent && topology === Consts.TOPOLOGY.INVERTING,
            nInvertingEnabled: false,
            svg: Consts.SVG.DiffADC_DiffSource_DiffAmp_Inverting,
            config: config.value,
        };
    }

    /**
     * Error configurations
     */
    @computed('circuitParts.adcType') get SEADC_CONFIG() {
        return {
            isCurrent: this.circuitParts.adcType === Consts.ADC_TYPE.SE,
            value: Consts.CONFIGURATION.SEADC,
        };
    }

    @computed('circuitParts', 'SEADC_CONFIG') get SEADC_SESource_DiffAmp() {
        let config = this.SEADC_CONFIG,
            circuitParts = this.circuitParts,
            isCurrent =
                circuitParts.adcType === Consts.ADC_TYPE.SE &&
                circuitParts.sourceType === Consts.SOURCE_TYPE.SE &&
                circuitParts.driverType === Consts.DRIVER_TYPE.DIFF_AMP;

        return {
            isCurrent: isCurrent,
            nInvertingEnabled: false,
            message: this.intl.t('messages.driver_se'),
            config: config.value,
        };
    }

    @computed('circuitParts', 'SEADC_CONFIG') get SEADC_DiffSource() {
        let config = this.SEADC_CONFIG,
            circuitParts = this.circuitParts,
            isCurrent =
                circuitParts.adcType === Consts.ADC_TYPE.SE && circuitParts.sourceType === Consts.SOURCE_TYPE.DIFF;

        return {
            isCurrent: isCurrent,
            inputSourceTypeError: true,
            message: this.intl.t('messages.source_se'),
            config: config.value,
        };
    }

    @computed('circuitParts.topology', 'DiffADC_DiffAmp_SESource_CONFIG')
    get DiffADC_DiffAmp_SESource_NonInvertingOrFollower() {
        let topology = this.circuitParts.topology,
            config = this.DiffADC_DiffAmp_SESource_CONFIG,
            isCurrent =
                config.isCurrent && [Consts.TOPOLOGY.FOLLOWER, Consts.TOPOLOGY.NON_INVERTING].includes(topology);

        return {
            isCurrent: isCurrent,
            nInvertingEnabled: false,
            message: this.intl.t('messages.circuit_diff'),
            config: config.value,
        };
    }

    @computed('circuitParts.topology', 'DiffADC_DiffAmp_DiffSource_CONFIG')
    get DiffADC_DiffSource_DiffAmp_NonInvertingOrFollower() {
        let topology = this.circuitParts.topology,
            config = this.DiffADC_DiffAmp_DiffSource_CONFIG,
            isCurrent =
                config.isCurrent && [Consts.TOPOLOGY.FOLLOWER, Consts.TOPOLOGY.NON_INVERTING].includes(topology);

        return {
            isCurrent: isCurrent,
            nInvertingEnabled: false,
            message: this.intl.t('messages.circuit_diff'),
            config: config.value,
        };
    }
}
